
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  CardContent,
  FormControl,
  Typography,
  TableRow,
  TableHead,
  Table,
  TableContainer,
  Paper,
  TableCell,
  TableBody,
  TablePagination,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Switch,
  Tooltip,
} from "@mui/material";

import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";

import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";
import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";

import TableSortLabel from "@mui/material/TableSortLabel";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

import { AddOrEditHoliday } from "../../../services/human-resource";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../components/container/PageContainer";

// Convert API date values to the format required by input[type="date"].


const toInputDate = (value) => {
  if (!value) return "";

  // Handle JavaScript Date objects
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  const dateString = String(value).trim();

  // Format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
  const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  // Format: DD-MM-YYYY or DD/MM/YYYY
  // Also supports: 12-10-2026 12:00:00 AM
  const localMatch = dateString.match(
    /^(\d{2})[-/](\d{2})[-/](\d{4})(?:\s+.*)?$/
  );

  if (localMatch) {
    const [, day, month, year] = localMatch;

    return `${year}-${month}-${day}`;
  }

  console.warn("Unrecognized date format:", value);
  return "";
};


const formatDisplayDate = (value) => {
  const date = toInputDate(value);

  if (!date) return "";

  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
};

// Important: Boolean("0") is true, so do not use Boolean() for DB flags.
const isFlagEnabled = (value) => {
  if (value === true || value === 1) return true;

  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();

  return ["1", "true", "yes"].includes(normalized);
};

const getHolidayDate = (item) =>
  item.holidayDate ??
  item.HolidayDate ??
  item.date ??
  item.Date ??
  "";

const getHolidayName = (item) =>
  item.name ??
  item.Name ??
  item.holidayName ??
  item.HolidayName ??
  item.designationName ??
  item.DesignationName ??
  "";

const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;

  if (typeof data === "string") return data;

  return (
    data?.message ||
    data?.Message ||
    data?.title ||
    fallback
  );
};

const HolidayList = () => {


  const BCrumb = [
        { to: "/", title: "HR Management" },
        { title: "Holiday" },
    ];

  const [list, setList] = useState([]);
  const [editId, setEditId] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  const [globalSearch, setGlobalSearch] = useState("");

  const submittingRef = useRef(false);

  // GET holidays
  const fetchHoliday = useCallback(async () => {
    try {
      const response = await AddOrEditHoliday({
        Action: "GET",
      });

      const result = response?.data;

      // Support common API response wrappers.
      const candidates = [
        result,
        result?.data,
        result?.Data,
        result?.result,
        result?.Result,
        result?.data?.data,
        result?.data?.Data,
        result?.Data?.data,
        result?.Data?.Data,
      ];

      const holidayData =
        candidates.find((value) => Array.isArray(value)) || [];

      setList(holidayData);

      return holidayData;
    } catch (error) {
      console.error(
        "Failed to fetch holidays:",
        error.response?.data || error
      );

      toast.error(
        getApiErrorMessage(error, "Failed to load holidays")
      );

      return [];
    }
  }, []);

  useEffect(() => {
    fetchHoliday();
  }, [fetchHoliday]);

  // Form validation
  const validationSchema = Yup.object({
    date: Yup.string().required("Date is required"),

    designation: Yup.string()
      .trim()
      .required("Name is required"),

    isAdjustedLeave: Yup.boolean(),

    adjustedLeaveDate: Yup.string().when("isAdjustedLeave", {
      is: true,
      then: (schema) =>
        schema.required("Adjusted leave date is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  // ADD / UPDATE
  
const formik = useFormik({
  initialValues: {
    date: "",
    designation: "",
    isAdjustedLeave: false,
    adjustedLeaveDate: "",
  },

  validationSchema,

  onSubmit: async (values, { resetForm, setSubmitting }) => {
    // Prevent duplicate submissions, including rapid clicks.
    if (submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);

    try {
      const payload = {
        Action: editId !== null ? "UPDATE" : "INSERT",
        ...(editId !== null ? { Id: editId } : {}),
        HolidayDate: values.date,
        Name: values.designation.trim(),
        AdjustedLeave: values.isAdjustedLeave ? 1 : 0,
        ReplacementDate: values.isAdjustedLeave
          ? values.adjustedLeaveDate
          : null,
        Active: 1,
      };

      console.log("Holiday request:", payload);

      // IMPORTANT: Call the API only once.
      await AddOrEditHoliday(payload);

      toast.success(
        editId !== null
          ? "Holiday updated successfully"
          : "Holiday added successfully"
      );

      await fetchHoliday();

      resetForm();
      setEditId(null);
      setPage(0);
    } catch (error) {
      console.error(
        "Failed to save holiday:",
        error.response?.data || error
      );

      toast.error(
        getApiErrorMessage(error, "Failed to save holiday")
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  },
});


  // Reset form
  const resetForm = () => {
    setEditId(null);
    formik.resetForm();
  };

  // DELETE (your stored procedure performs a soft delete)
  const handleDelete = async (id) => {
    if (id == null) {
      toast.error("Holiday Id is missing");
      return;
    }

    try {
      await AddOrEditHoliday({
        Action: "DELETE",
        Id: id,
      });

      toast.success("Holiday deleted successfully");

      await fetchHoliday();

      if (editId != null && String(editId) === String(id)) {
        resetForm();
      }
    } catch (error) {
      console.error(
        "Failed to delete holiday:",
        error.response?.data || error
      );

      toast.error(
        getApiErrorMessage(error, "Failed to delete holiday")
      );
    }
  };

const handleEdit = (item) => {
  console.log("Selected holiday:", item);
  console.log("API holiday date:", getHolidayDate(item));

  const itemId = item.id ?? item.Id;

  setEditId(itemId);

  const adjustedValue =
    item.adjustedLeave ??
    item.AdjustedLeave ??
    item.isAdjustedLeave ??
    item.IsAdjustedLeave;

  const holidayDate = toInputDate(getHolidayDate(item));

  const replacementDate = toInputDate(
    item.replacementDate ??
    item.ReplacementDate ??
    item.adjustedLeaveDate ??
    item.AdjustedLeaveDate
  );

  console.log("Formatted holiday date:", holidayDate);

  formik.setValues({
    date: holidayDate,
    designation: getHolidayName(item),
    isAdjustedLeave: isFlagEnabled(adjustedValue),
    adjustedLeaveDate: replacementDate,
  });

  formik.setTouched({});

  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};


  // ACTIVE / INACTIVE STATUS
  const handleStatusChange = async (item, newStatus) => {
    const itemId = item.id ?? item.Id;

    if (itemId == null) {
      toast.error("Holiday Id is missing");
      return;
    }

    try {
      await AddOrEditHoliday({
        Action: "UPDATE",
        Id: itemId,
        Active: newStatus ? 1 : 0,
      });

      toast.success(
        `Holiday ${newStatus ? "activated" : "deactivated"} successfully`
      );

      await fetchHoliday();
    } catch (error) {
      console.error(
        "Failed to update holiday status:",
        error.response?.data || error
      );

      toast.error(
        getApiErrorMessage(error, "Failed to update holiday status")
      );
    }
  };

  // PAGINATION
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  // SORTING
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";

    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getSortValue = (item, property) => {
    if (property === "date") {
      return toInputDate(getHolidayDate(item));
    }

    if (property === "name") {
      return getHolidayName(item);
    }

    if (property === "adjustedLeave") {
      const value =
        item.adjustedLeave ??
        item.AdjustedLeave ??
        item.isAdjustedLeave ??
        item.IsAdjustedLeave;

      return isFlagEnabled(value) ? "yes" : "no";
    }

    return item[property] ?? "";
  };

  // SEARCH
  const searchedList = list.filter((item) => {
    const name = getHolidayName(item);
    const date = toInputDate(getHolidayDate(item));

    return `${name} ${date}`
      .toLowerCase()
      .includes(globalSearch.trim().toLowerCase());
  });

  // SORTED LIST
  const sortedList = [...searchedList].sort((a, b) => {
    if (!orderBy) return 0;

    const valueA = String(getSortValue(a, orderBy)).toLowerCase();
    const valueB = String(getSortValue(b, orderBy)).toLowerCase();

    if (valueA < valueB) return order === "asc" ? -1 : 1;
    if (valueA > valueB) return order === "asc" ? 1 : -1;

    return 0;
  });

  // TABLE HEADER
const renderHeader = (label, field, width) => (
  <TableCell
    key={field}
    align="center"
    sx={{ fontWeight: 600, fontSize: "0.875rem", width }}
  >
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={0.5}
      sx={{ color: "text.primary" }}
    >
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? order : "asc"}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </Box>
  </TableCell>
);
  

  return (
    <PageContainer title="Master Management">
      <Breadcrumb title="Holiday" items={BCrumb} />
      {/* ADD / EDIT HOLIDAY */}
      <Card
        sx={{
          mb: 2,
          p: 0,
          overflow: "hidden",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="alert-circle" width="18" />

          <Box sx={{ ml: 1 }}>
            {editId !== null ? "Edit Holiday" : "Add Holiday"}
          </Box>
        </Box>

        <FormControl
          component="form"
          sx={{ width: "100%" }}
          onSubmit={formik.handleSubmit}
        >
          <CardContent sx={{ padding: "10px 30px" }}>
            <Grid container spacing={2} alignItems="flex-start">
              {/* HOLIDAY DATE */}
              <Grid item lg={2} md={6} sm={12} xs={12}>
                <CustomFormLabel>Date</CustomFormLabel>

                <CustomTextField
                  type="date"
                  name="date"
                  value={formik.values.date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={
                    formik.touched.date &&
                    Boolean(formik.errors.date)
                  }
                  helperText={
                    formik.touched.date && formik.errors.date
                  }
                />
              </Grid>

              {/* HOLIDAY NAME */}
              <Grid item lg={3} md={6} sm={12} xs={12}>
                <CustomFormLabel>Name</CustomFormLabel>

                <CustomTextField
                  name="designation"
                  value={formik.values.designation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  size="small"
                  error={
                    formik.touched.designation &&
                    Boolean(formik.errors.designation)
                  }
                  helperText={
                    formik.touched.designation &&
                    formik.errors.designation
                  }
                />
              </Grid>

              {/* ADJUSTED LEAVE CHECKBOX */}
              {/* ADJUSTED LEAVE CHECKBOX */}
<Grid
  item
  lg={2}
  md={6}
  sm={12}
  xs={12}
  sx={{
    display: "flex",
    alignItems: "center",
    height: "100%",
    mt: 5, // roughly the label height ("Date"/"Name"), so it lines up with the inputs
  }}
>
  <FormControlLabel
    sx={{ m: 0 }}
    control={
      <Checkbox
        name="isAdjustedLeave"
        checked={formik.values.isAdjustedLeave}
        onChange={(event) => {
          const checked = event.target.checked;

          formik.setFieldValue("isAdjustedLeave", checked);

          if (!checked) {
            formik.setFieldValue("adjustedLeaveDate", "");
            formik.setFieldTouched("adjustedLeaveDate", false);
          }
        }}
      />
    }
    label="Is Adjusted Leave"
  />
</Grid>

              {/* SHOW REPLACEMENT DATE ONLY WHEN CHECKED */}
              {formik.values.isAdjustedLeave && (
                <Grid item lg={2} md={6} sm={12} xs={12}>
                  <CustomFormLabel>
                    Adjusted Leave Date
                  </CustomFormLabel>

                  <CustomTextField
                    type="date"
                    name="adjustedLeaveDate"
                    value={formik.values.adjustedLeaveDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    error={
                      formik.touched.adjustedLeaveDate &&
                      Boolean(formik.errors.adjustedLeaveDate)
                    }
                    helperText={
                      formik.touched.adjustedLeaveDate &&
                      formik.errors.adjustedLeaveDate
                    }
                  />
                </Grid>
              )}

              {/* BUTTONS */}
              <Grid
                item
                lg={2}
                md={6}
                sm={12}
                xs={12}
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  mt: { xs: 1, lg: 5 },
                  gap: 2,
                }}
              >
                <Button
                  variant="contained"
                  type="submit"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting
                    ? "Saving..."
                    : editId !== null
                      ? "Update"
                      : "Add"}
                </Button>

                <Button
                  variant="outlined"
                  type="button"
                  onClick={resetForm}
                  disabled={formik.isSubmitting}
                >
                  {editId !== null ? "Cancel" : "Reset"}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </FormControl>
      </Card>

      {/* HOLIDAY LIST */}
      <Card
        sx={{
          mt: 2,
          p: 0,
          overflow: "hidden",
        }}
      >
        {/* LIST HEADER */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <Box display="flex" alignItems="center">
            <FeatherIcon icon="alert-circle" width="18" />
            <Box sx={{ ml: 1 }}>Holiday List</Box>
          </Box>

          {/* SEARCH */}
          <Box display="flex" alignItems="center">
            <TextField
              size="small"
              placeholder="Search..."
              value={globalSearch}
              onChange={(event) => {
                setGlobalSearch(event.target.value);
                setPage(0);
              }}
              sx={{
                width: 220,
                backgroundColor: "white",
                borderRadius: 1,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {globalSearch && (
              <CloseIcon
                sx={{
                  cursor: "pointer",
                  ml: 1,
                }}
                onClick={() => {
                  setGlobalSearch("");
                  setPage(0);
                }}
              />
            )}
          </Box>
        </Box>

        <CardContent>
          {sortedList.length === 0 ? (
            <Typography>
              {globalSearch
                ? "No matching holidays found"
                : "No Holidays Found"}
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #EEF0F2" }}>
  <Table sx={{ minWidth: 650 }}>
    <TableHead>
      <TableRow sx={{ backgroundColor: "#FAFBFB" }}>
        {renderHeader("Date", "date", { align: "center" })}
        {renderHeader("Name", "name", { align: "center" })}
        {renderHeader("Adjusted Leave", "adjustedLeave", { align: "center" })}
        {renderHeader("Adjusted Leave Date", "adjustedLeaveDate", { align: "center" })}
        <TableCell align="center" sx={{ fontWeight: 600 }}>
          Action
        </TableCell>
      </TableRow>
    </TableHead>

    <TableBody>
      {sortedList
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((item) => {
          const itemId = item.id ?? item.Id;

          const holidayDate = formatDisplayDate(getHolidayDate(item));

          const adjustedDate = item.replacementDate ?? item.Replacementdate ?? null;
          const datePart = adjustedDate ? adjustedDate.split(" ")[0] : "";

          const holidayName = getHolidayName(item) || "-";

          const adjustedValue =
            item.adjustedLeave ??
            item.AdjustedLeave ??
            item.isAdjustedLeave ??
            item.IsAdjustedLeave;

          const isAdjustedLeave = isFlagEnabled(adjustedValue);

          const activeValue =
            item.active ?? item.Active ?? item.isActive ?? item.IsActive;

          const isActive = isFlagEnabled(activeValue);

          return (
            <TableRow
              key={itemId}
              hover
              sx={{ "& td": { py: 1.5 } }}
            >
              <TableCell align="center">{holidayDate}</TableCell>

              <TableCell align="center">{holidayName}</TableCell>

              <TableCell align="center">
                {isAdjustedLeave ? "Yes" : "No"}
              </TableCell>

              <TableCell align="center">{datePart || "-"}</TableCell>

              <TableCell align="center">
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                >
                  <Tooltip title="Edit">
                    <EditIcon
                      sx={{ cursor: "pointer", color: "primary.main" }}
                      onClick={() => handleEdit(item)}
                    />
                  </Tooltip>

                  <Tooltip title={isActive ? "Active" : "Inactive"}>
                    <Switch
                      checked={isActive}
                      size="small"
                      onChange={(event) =>
                        handleStatusChange(item, event.target.checked)
                      }
                    />
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          );
        })}
    </TableBody>
  </Table>
</TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={sortedList.length}
                rowsPerPage={rowsPerPage}
                page={Math.min(
                  page,
                  Math.max(
                    0,
                    Math.ceil(sortedList.length / rowsPerPage) - 1
                  )
                )}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                showFirstButton
                showLastButton
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* DELETE CONFIRMATION */}
      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>

        <DialogContent>
          Are you sure you want to delete this holiday?
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              await handleDelete(deleteId);
              setOpenDelete(false);
              setDeleteId(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default HolidayList;